<template>
  <div
    v-if="visibleElements.length"
    class="relative transition-colors duration-500"
    :class="currentBackground"
  >
    <SimpleCarousel
      :slider-items="visibleElements"
      :per-page="1"
      :active-index="activeIndex"
    >
      <template #default="{ currentItem }">
        <BannerProposition
          :campaign-name="currentItem.campaignName"
          :page-category="previousPageType"
          :banner-text="currentItem.bannerText"
          :banner-text-bold="currentItem.bannerTextBold"
          :banner-text-color="currentItem.bannerTextColor"
          :additional-banner-text="currentItem.additionalBannerText"
          :additional-banner-text-color="currentItem.additionalBannerTextColor"
          :additional-banner-text-bold="currentItem.additionalBannerTextBold"
          :mobile-content-align="currentItem.mobileContentAlign"
          :banner-button-text="currentItem.bannerButtonText"
          :banner-button-text-bold="currentItem.bannerButtonTextBold"
          :banner-button-text-underline="currentItem.bannerButtonTextUnderline"
          :banner-button-text-color="currentItem.bannerButtonTextColor"
          :banner-button-background-color="currentItem.bannerButtonBackgroundColor"
          :icon="currentItem.icon"
          :banner-background="currentItem.bannerBackground"
          :banner-url="currentItem.bannerUrl"
          :show-nav-buttons="showNavButtons"
          @click-prev="previousHandler"
          @click-next="nextHandler"
        />
      </template>
    </SimpleCarousel>
  </div>
</template>

<script lang="ts">
import { usePreferredReducedMotion } from '@vueuse/core';
import BannerProposition from '~/components/molecules/PromoBanner/BannerProposition.vue';
import type { UniqueBannerProposition } from '~~/types/cms/UniqueBannerPropositions';
import SimpleCarousel from '~/components/molecules/SimpleCarousel/SimpleCarousel.vue';
import { PROMO_BANNER_INTERVAL_MILISECONDS } from '~~/constants/timeUnits';

export default defineComponent({
  name: 'PromoBanners',
  components: {
    BannerProposition,
    SimpleCarousel,
  },
  props: {
    autoRotate: {
      type: Boolean,
      default: true,
    },
    elements: {
      type: Array as PropType<UniqueBannerProposition[]>,
      default: () => {

        return []; // empty content is acceptable so as to hide the component

      },
      validator: (uniqueBannerPropositions: UniqueBannerProposition[]) => {

        return uniqueBannerPropositions.every((element: UniqueBannerProposition) => {

          return element.campaignName && element.bannerText;

        });

      },
    },
  },
  setup(props) {

    const pageTypeHistory = ref([]);
    const { getCurrentPageType } = usePageEventLocation();
    const currentPageType = computed(() => getCurrentPageType());
    const previousPageType = computed(() => {

      pageTypeHistory.value.unshift(currentPageType.value);
      pageTypeHistory.value = pageTypeHistory.value.slice(0, 2);

      return pageTypeHistory.value.at(-1);

    });
    const { isResolvable } = usePromoBannerPlaceholders();
    const visibleElements = computed(() => props.elements
      .filter(element => isResolvable(element.bannerText)));
    const activeIndex = ref(0);
    const showPrevious = () => {

      activeIndex.value = activeIndex.value === 0
        ? visibleElements.value.length - 1
        : activeIndex.value - 1;

    };
    const showNext = () => {

      activeIndex.value = activeIndex.value === visibleElements.value.length - 1
        ? 0
        : activeIndex.value + 1;

    };
    const showNavButtons = computed(() => visibleElements.value.length > 1);
    const currentBackground = computed(() => {

      return visibleElements.value[activeIndex.value]?.bannerBackground
        ? visibleElements.value[activeIndex.value].bannerBackground
        : 'bg-EXPONDO-primary';

    });
    const preferredMotion = usePreferredReducedMotion();
    const shouldAutoRotate = computed(() => props.autoRotate && preferredMotion.value !== 'reduce');
    let rotationInterval: ReturnType<typeof setInterval> | undefined;

    const stopRotation = () => {

      clearInterval(rotationInterval);

    };
    const startRotation = () => {

      stopRotation();

      if (shouldAutoRotate.value) {

        rotationInterval = setInterval(showNext, PROMO_BANNER_INTERVAL_MILISECONDS);

      }

    };
    const previousHandler = () => {

      showPrevious();
      startRotation();

    };
    const nextHandler = () => {

      showNext();
      startRotation();

    };

    onMounted(startRotation);

    onBeforeUnmount(stopRotation);

    return {
      previousHandler,
      nextHandler,
      activeIndex,
      visibleElements,
      showNavButtons,
      currentBackground,
      previousPageType,
    };

  },
});
</script>

<style scoped lang="scss">
.banner-bg-extension {
  left: calc(-50vw + 50%);
}
</style>
